// Shared Escape stack for overlays (Modal, Lightbox). Every overlay-level
// keydown listener on `document` fires on the same target, so multiple open
// overlays would all close on one Esc. Instead we keep a single global listener
// and a stack: Esc closes ONLY the topmost overlay (and stops propagation so a
// nested Lightbox inside a Modal doesn't tear down both).
// Canceling the keydown also keeps a native <dialog> (Modal.jsx) from closing itself on that Esc.
const stack = [];
let installed = false;
let escAt = -Infinity;

function onKey(e) {
  if (e.key !== 'Escape' || !stack.length) return;
  e.stopPropagation();
  e.preventDefault();
  escAt = Date.now();
  stack[stack.length - 1]();
}

// A close request that isn't our Esc — Android's back gesture on an open <dialog>, which
// reaches Modal as a `cancel` event — closes the topmost overlay too, by the same rule. Never
// twice for one key: an engine that still sent `cancel` for the Esc just handled is ignored.
export function closeTopOverlay() {
  if (!stack.length || Date.now() - escAt < 100) return;
  stack[stack.length - 1]();
}

// Push a close handler; returns a disposer that removes it. Call on overlay mount.
export function pushEscHandler(handler) {
  if (!installed) {
    document.addEventListener('keydown', onKey, true); // capture: run before app-level handlers
    installed = true;
  }
  stack.push(handler);
  return () => {
    const i = stack.lastIndexOf(handler);
    if (i >= 0) stack.splice(i, 1);
  };
}

// The <dialog>s open with showModal() (Modal.jsx), in top-layer order. While one is up the
// browser makes everything outside the topmost inert — a top-layer popover shown after it
// included — so an overlay that must stay usable over a modal (the PWA update banner) renders
// inside it: useSyncExternalStore(subscribeModals, modalsState). `seq` moves on every change,
// a dialog re-shown in place (reopened after a native close) included.
const modals = [];
const modalListeners = new Set();
let snapshot = { top: null, seq: 0 };
function emitModals() {
  snapshot = { top: modals[modals.length - 1] || null, seq: snapshot.seq + 1 };
  modalListeners.forEach((fn) => fn());
}

// A dialog just shown with showModal(): on top of the top layer, so listed last.
export function modalOpened(dialog) {
  const i = modals.indexOf(dialog);
  if (i >= 0) modals.splice(i, 1);
  modals.push(dialog);
  emitModals();
}

export function modalClosed(dialog) {
  const i = modals.indexOf(dialog);
  if (i < 0) return;
  modals.splice(i, 1);
  emitModals();
}

export const openModals = () => [...modals]; // bottom to top
export const modalsState = () => snapshot;

export function subscribeModals(fn) {
  modalListeners.add(fn);
  return () => modalListeners.delete(fn);
}

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

// Visible, focusable descendants of `container`, in DOM order.
export function focusables(container) {
  if (!container) return [];
  return [...container.querySelectorAll(FOCUSABLE)].filter((n) => n.offsetParent !== null);
}
